/**
 * Quão velho está o dado — funções puras (testadas em `frescor.test.ts`).
 *
 * Dois relógios diferentes:
 * - a PUBLICAÇÃO do espelho (`gerado`): o passo do radar roda no ciclo da
 *   chuva do `AmaView-dados`, a cada 15 min;
 * - a VARREDURA mais nova de cada radar: o SIPAM publica com 10–21 min de
 *   atraso, então uma varredura de 25 min atrás é normal; de 1 h, não.
 */

/** Cadência nominal do espelho. */
export const CADENCIA_MIN = 15;
/** Três ciclos perdidos. */
export const ATRASO_AVISO_MIN = CADENCIA_MIN * 3;
/** Seis horas: o que está no mapa é história. */
export const ATRASO_GRAVE_MIN = 6 * 60;
/**
 * Radar calado há mais que isto: marcado na lista. Latência normal (até
 * ~21 min) + um ciclo do espelho (15) + a CDN (até 10) + folga.
 */
export const RADAR_CALADO_MIN = 60;

export type Frescor = 'ok' | 'atrasado' | 'velho' | 'desconhecido';

export function atrasoMin(ms: number | null, agora: number = Date.now()): number | null {
  if (ms === null || !Number.isFinite(ms)) return null;
  // Relógio do visitante adiantado não vira "atraso negativo".
  return Math.max(0, (agora - ms) / 60_000);
}

export function frescorDe(gerado: number | null, agora: number = Date.now()): Frescor {
  const min = atrasoMin(gerado, agora);
  if (min === null) return 'desconhecido';
  if (min >= ATRASO_GRAVE_MIN) return 'velho';
  if (min >= ATRASO_AVISO_MIN) return 'atrasado';
  return 'ok';
}

function quanto(min: number): string {
  return min >= 120 ? `${Math.floor(min / 60)} h` : `${Math.round(min)} min`;
}

/** Aviso sobre a publicação do espelho, ou null quando está em dia. */
export function avisoDeFrescor(gerado: number | null, agora: number = Date.now()): string | null {
  const f = frescorDe(gerado, agora);
  if (f === 'ok' || f === 'desconhecido') return null;
  const min = atrasoMin(gerado, agora)!;
  return f === 'velho'
    ? `O espelho dos radares está parado há ${quanto(min)}. O que está no mapa não é a chuva de agora.`
    : `Radares sem atualizar há ${quanto(min)} — o normal é a cada ${CADENCIA_MIN} min.`;
}

/** O radar está sem varredura nova há tempo demais? */
export function radarCalado(ultima: number | null, agora: number = Date.now()): boolean {
  const min = atrasoMin(ultima, agora);
  return min !== null && min >= RADAR_CALADO_MIN;
}
